import { isSameDay, startOfWeek } from "date-fns";
import { useContext, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { FILTER_DEFAULTS } from "features/allocations/hooks/useAllocationsPermission";
import FilterContext from "features/allocations/contexts/FilterContext";
import { allocationsGridModes } from "shared/constants/allocationsEnums";
import moment from "moment";
import { useApi } from "shared/services/api/useApi";
import { useCrypto } from "shared/hooks/crypto/useCrypto";

const toArrayOrNull = (value) => {
    if (value === null || value === undefined) return null;
    return Array.isArray(value) ? value : [value];
};

export const useAllocationsFilters = () => {
    const filterConsumer = useContext(FilterContext);
    const location = useLocation();
    let query = {};
    let [searchParams, setSearchParams] = useSearchParams(location.search);
    const { getDataAsync } = useApi();
    const { encrypt, decrypt } = useCrypto();

    const buildQuery = (headerSelection, filters) => {
        let params = {};
        const dateFrom = moment(headerSelection.startDate).format("YYYY-MM-DD");
        const dateTo = moment(headerSelection.endDate).format("YYYY-MM-DD");
        const mode = allocationsGridModes.weekly;
        const page = headerSelection.page;
        const pageSize = globalThis.config.pagination.pageSize;
        const sortBy = "Lastname";
        const sortOrder = "asc";
        const userId = headerSelection.userId
            ? headerSelection.userId.join(",")
            : null;
        const customGroupId = headerSelection.customGroupId
            ? headerSelection.customGroupId
            : null;
        const sd = moment(headerSelection.startDate).toDate();
        const td = startOfWeek(moment().toDate(), { weekStartsOn: 1 });

        if (!isSameDay(sd, td)) {
            params.dateFrom = dateFrom;
        }

        if (userId) {
            params.userId = userId;
        }

        if (filters?.fromUrl) {
            query = {
                dateFrom,
                dateTo,
                mode,
                page,
                pageSize,
                sortBy,
                sortOrder,
                //groupBy,
                ...filters,
            };
        } else {
            const agencyId = filters?.agency?.value ?? null;
            const locations = filters?.locations;
            const regionId =
                toArrayOrNull(filters?.regions)
                    ?.map((region) => region.value)
                    .join(",") ?? null;
            const officeId =
                toArrayOrNull(filters?.offices)
                    ?.map((office) => office.value)
                    .join(",") ?? null;
            const groupId =
                toArrayOrNull(filters?.groups)
                    ?.map((group) => group.value)
                    .join(",") ?? null;
            const departmentId =
                toArrayOrNull(filters?.departments)
                    ?.map((department) => department.value)
                    .join(",") ?? null;
            const titleId =
                toArrayOrNull(filters?.titles)
                    ?.map((title) => title.value)
                    .join(",") ?? null;
            const clientFamilyId =
                toArrayOrNull(filters?.clientFamily)
                    ?.map((x) => x.value)
                    .join(",") ?? null;
            const clientId =
                toArrayOrNull(filters?.client)
                    ?.map((x) => x.value)
                    .join(",") ?? null;
            const retainerId = filters?.retainer?.value ?? null;
            const jobId = filters?.project?.value ?? null;
            const display = filters?.display?.join(",") ?? null;

            query = {
                dateFrom,
                dateTo,
                mode,
                page,
                pageSize,
                sortBy,
                sortOrder,
                //groupBy,
                locations,
                agencyId,
                regionId,
                officeId,
                groupId,
                departmentId,
                titleId,
                clientFamilyId,
                clientId,
                retainerId,
                jobId,
                display,
                userId,
                customGroupId,
            };
        }

        filterConsumer.summaryQuery = query;
        const filterParams = buildURLParams(query);
        if (query.customGroupId) {
            params.customGroupId = customGroupId;
        } else if (filterParams) {
            params.filters = encrypt(filterParams);
        }

        setSearchParams(params);
        // The default agency only applies to the general browse/filter view to
        // avoid loading every agency at once. When the query targets a specific
        // employee (userId) or a custom group, we must NOT constrain by agency,
        // otherwise people in other agencies are silently excluded by the SP.
        if (query.agencyId === null && !query.userId && !query.customGroupId) {
            query.agencyId = FILTER_DEFAULTS.agencyId;
        }
        return query;
    };

    const buildQueryEstimatedResults = (headerSelection, filters) => {
        const dateFrom = moment(headerSelection.startDate).format("YYYY-MM-DD");
        const dateTo = moment(headerSelection.endDate).format("YYYY-MM-DD");
        const mode = allocationsGridModes.weekly;

        const groupBy = headerSelection.groupBy
            ? headerSelection.groupBy
            : null;

        const agencyId = filters?.agency
            ? filters.agency?.value
            : FILTER_DEFAULTS.agencyId;

        const regionId =
            toArrayOrNull(filters?.regions)
                ?.map((region) => region.value)
                .join(",") ?? null;

        const officeId =
            toArrayOrNull(filters?.offices)
                ?.map((office) => office.value)
                .join(",") ?? null;

        const groupId =
            toArrayOrNull(filters?.groups)
                ?.map((group) => group.value)
                .join(",") ?? null;

        const departmentId =
            toArrayOrNull(filters?.departments)
                ?.map((department) => department.value)
                .join(",") ?? null;

        const titleId =
            toArrayOrNull(filters?.titles)
                ?.map((title) => title.value)
                .join(",") ?? null;

        const clientFamilyId =
            toArrayOrNull(filters?.clientFamily)
                ?.map((x) => x.value)
                .join(",") ?? null;

        const clientId =
            toArrayOrNull(filters?.client)
                ?.map((x) => x.value)
                .join(",") ?? null;

        const retainerId = filters?.retainer ? filters.retainer.value : null;

        const jobId = filters?.project ? filters.project.value : null;

        const display = filters?.display ? filters.display.join(",") : null;
        query = {
            dateFrom,
            dateTo,
            mode,
            groupBy,
            agencyId,
            regionId,
            officeId,
            groupId,
            departmentId,
            titleId,
            clientFamilyId,
            clientId,
            retainerId,
            jobId,
            display,
        };
        return query;
    };

    const buildURLParams = (filters) => {
        if (filters == null) return;
        const {
            dateFrom,
            dateTo,
            sortOrder,
            sortBy,
            groupBy,
            groupById,
            userId,
            customGroupId,
            mode,
            page,
            pageSize,
            fromUrl,
            locations,
            ...p
        } = filters;

        let params = Object.entries(p)
            .filter(([_, value]) => value != null && value !== "")
            .map(([key, val]) => key + "=" + val);

        if (params.length === 0) return;

        const locationsParam = Array.isArray(filters?.locations)
            ? filters.locations.join("_")
            : filters.locations;

        if (filters?.locations?.length) {
            params.push("locations=" + locationsParam);
        }

        const urlParams = params.join(";");
        return urlParams;
    };

    const getFilterDescription = async (params) => {
        const payload = await getDataAsync("filterDescription", {
            params: params,
        });
        const data = await payload.json();
        // Backend returns collection fields in plural (`clients`, `clientFamilies`);
        // the rest of the frontend uses singular (`client`, `clientFamily`) to
        // align with the URL param names. Map here so the rest of the modal sees
        // a consistent shape. Also defensively wrap in arrays in case the backend
        // ever returns a single object instead of an array.
        return {
            ...data,
            regions: toArrayOrNull(data?.regions) ?? [],
            offices: toArrayOrNull(data?.offices) ?? [],
            groups: toArrayOrNull(data?.groups) ?? [],
            departments: toArrayOrNull(data?.departments) ?? [],
            titles: toArrayOrNull(data?.titles) ?? [],
            clientFamily:
                toArrayOrNull(data?.clientFamilies ?? data?.clientFamily) ?? [],
            client: toArrayOrNull(data?.clients ?? data?.client) ?? [],
        };
    };

    const buildQueryFromParams = (selection) => {
        const p = Object.fromEntries(searchParams);
        const { filters, ...result } = p;
        let filtersParams = {};
        if (searchParams.has("filters")) {
            filtersParams = Object.fromEntries(
                decrypt(p.filters)
                    .split(";")
                    .map((param) => [param.split("=")[0], param.split("=")[1]])
            );
            filtersParams.fromUrl = true;
        }

        selection.page = 1;
        if (searchParams.has("sortBy")) {
            selection.sortBy = result.sortBy + "," + result.sortOrder;
        }

        if (searchParams.has("dateFrom")) {
            selection.startDate = moment(result.dateFrom).format("YYYY-MM-DD");
        }
        return filtersParams;
    };

    const methods = useMemo(() => {
        return {
            buildQuery,
            buildQueryEstimatedResults,
            buildURLParams,
            getFilterDescription,
            buildQueryFromParams,
        };
        // Each function is a plain inline closure recreated every render — adding them as deps
        // would re-run useMemo on every render, defeating the stable-reference intent.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return methods;
};
